import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { NewProgram } from "../../../shared/src";
import {
  ApiError,
  archiveProgram,
  createProgram,
  listPrograms,
  unarchiveProgram,
} from "../api-client";

const PROGRAMS_KEY = ["programs"] as const;

interface FormState {
  name: string;
  notes: string;
}

const EMPTY_FORM: FormState = { name: "", notes: "" };

function toNewProgram(form: FormState): NewProgram {
  return {
    name: form.name.trim(),
    notes: form.notes.trim() || null,
  };
}

export function Programs() {
  const queryClient = useQueryClient();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [createError, setCreateError] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: [...PROGRAMS_KEY, { includeArchived }],
    queryFn: () => listPrograms({ includeArchived }),
  });

  function invalidateList() {
    return queryClient.invalidateQueries({ queryKey: PROGRAMS_KEY });
  }

  const createMutation = useMutation({
    mutationFn: (input: NewProgram) => createProgram(input),
    onSuccess: () => invalidateList(),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: number) => archiveProgram(id),
    onSuccess: () => invalidateList(),
  });

  const unarchiveMutation = useMutation({
    mutationFn: (id: number) => unarchiveProgram(id),
    onSuccess: () => invalidateList(),
  });

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    if (form.name.trim().length === 0) return;
    setCreateError(null);
    try {
      await createMutation.mutateAsync(toNewProgram(form));
      setForm(EMPTY_FORM);
    } catch (err) {
      setCreateError(messageFor(err));
    }
  }

  return (
    <main className="min-h-dvh p-6 space-y-6">
      <header className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold">Programs</h1>
        <Link
          to="/dashboard"
          className="text-sm text-slate-400 hover:text-slate-200"
        >
          ← Dashboard
        </Link>
      </header>

      <form
        onSubmit={onCreate}
        className="rounded-lg border border-slate-800 bg-slate-900 p-4 space-y-3"
        aria-label="new program"
      >
        <h2 className="text-sm font-medium text-slate-300">New program</h2>
        <label className="block">
          <span className="text-xs text-slate-400">Name</span>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            maxLength={80}
            aria-label="name"
            className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-base"
          />
        </label>
        <label className="block">
          <span className="text-xs text-slate-400">Notes</span>
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            maxLength={1000}
            rows={2}
            aria-label="notes"
            className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-base"
          />
        </label>
        {createError && (
          <p className="text-sm text-red-400" role="alert">
            {createError}
          </p>
        )}
        <div className="flex items-center justify-end">
          <button
            type="submit"
            disabled={
              createMutation.isPending || form.name.trim().length === 0
            }
            className="rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-500 disabled:opacity-50"
          >
            {createMutation.isPending ? "Adding…" : "Add program"}
          </button>
        </div>
      </form>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-slate-300">
            {listQuery.data ? `${listQuery.data.length} programs` : "Loading…"}
          </h2>
          <label className="flex items-center gap-2 text-sm text-slate-400">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            Show archived
          </label>
        </div>

        {listQuery.isError && (
          <p className="text-sm text-red-400" role="alert">
            Failed to load programs.
          </p>
        )}

        {listQuery.data && listQuery.data.length === 0 && (
          <p className="text-sm text-slate-400">
            No programs yet. Add one above to get started.
          </p>
        )}

        <ul className="space-y-2">
          {listQuery.data?.map((p) => (
            <li
              key={p.id}
              className="rounded-md border border-slate-800 bg-slate-900"
            >
              <div className="flex items-center justify-between gap-3 p-3">
                <Link to={`/programs/${p.id}`} className="flex-1 text-left">
                  <div className="text-base font-medium">
                    {p.name}
                    {p.archived && (
                      <span className="ml-2 text-xs text-slate-500">
                        (archived)
                      </span>
                    )}
                  </div>
                  {p.notes && (
                    <div className="text-xs text-slate-400 line-clamp-1">
                      {p.notes}
                    </div>
                  )}
                </Link>
                {p.archived ? (
                  <button
                    type="button"
                    onClick={() => unarchiveMutation.mutate(p.id)}
                    disabled={unarchiveMutation.isPending}
                    className="rounded-md border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
                  >
                    Unarchive
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => archiveMutation.mutate(p.id)}
                    disabled={archiveMutation.isPending}
                    className="rounded-md border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
                  >
                    Archive
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}

function messageFor(err: unknown): string {
  if (err instanceof ApiError && err.code === "BAD_REQUEST") return err.message;
  return "Something went wrong. Try again.";
}
